import { useCallback, useMemo } from "react";

import { useNavigate } from "react-router-dom";
import { ApolloError } from "@apollo/client";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";

import { useAuth } from "../../context/AuthContext";
import routeNames from "../../constants/route-names";
import AppButton from "../../components/app-button/app-button";
import { useGetUserQuery } from "../../graphql/queries/getUser.generated";

import styles from "./home.style";
import "./home.css";

const HomePage = () => {
  const navigate = useNavigate();
  const { loggedUser } = useAuth();

  const navigateToLogin = useCallback(() => {
    navigate(routeNames.LOGIN);
  }, [navigate]);

  const navigateToCreateNotification = useCallback(() => {
    navigate(routeNames.CREATE_NOTIFICATION);
  }, [navigate]);

  const navigateToAiChats = useCallback(() => {
    navigate(routeNames.AI_CHATS);
  }, [navigate]);

  const navigateToGrit = useCallback(() => {
    navigate(routeNames.GRIT);
  }, [navigate]);

  const navigateToManageUsers = useCallback(() => {
    navigate(routeNames.MANAGE_USERS);
  }, [navigate]);

  const navigateToMentorshipUsers = useCallback(() => {
    navigate(routeNames.MENTORSHIP_USERS);
  }, [navigate]);

  const handleOnErrorsGetUsers = useCallback((error: ApolloError) => {
    console.log("Error Get Users: ", error);
  }, []);

  const { data, loading } = useGetUserQuery({
    skip: !loggedUser?.uid,
    variables: {
      id: loggedUser?.uid,
    },
    errorPolicy: "none",
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
    onError: handleOnErrorsGetUsers,
  });

  const buttonsContainer = useMemo(() => {
    const { isHighnoteAdmin, isWorkspaceAdmin } = data?.users_by_pk || {};

    if (loading) {
      return (
        <Box sx={styles.loadingContainer}>
          <CircularProgress />
        </Box>
      );
    }

    if (Boolean(loggedUser) === false) {
      return <AppButton onClick={navigateToLogin} label={"Sign In"} />;
    }

    return (
      <>
        {isHighnoteAdmin ? (
          <AppButton
            onClick={navigateToCreateNotification}
            label={"Create Notification"}
          />
        ) : null}

        {Boolean(loggedUser) && (isHighnoteAdmin || isWorkspaceAdmin) ? (
          <AppButton
            onClick={navigateToAiChats}
            label={"Talk with your data"}
          />
        ) : null}

        {Boolean(loggedUser) ? (
          <AppButton onClick={navigateToGrit} label={"GRIT"} />
        ) : null}

        {Boolean(loggedUser) && (isHighnoteAdmin || isWorkspaceAdmin) ? (
          <AppButton onClick={navigateToManageUsers} label={"Manage Users"} />
        ) : null}

        {/* {Boolean(loggedUser) && (isHighnoteAdmin || isWorkspaceAdmin) ? (
          <AppButton
            onClick={navigateToMentorshipUsers}
            label={"Mentorship Users"}
          />
        ) : null} */}
      </>
    );
  }, [
    loading,
    loggedUser,
    navigateToLogin,
    navigateToCreateNotification,
    navigateToAiChats,
    navigateToGrit,
    navigateToManageUsers,
    navigateToMentorshipUsers,
    data?.users_by_pk,
  ]);

  return (
    <Container maxWidth="xs" sx={styles.container}>
      <Grid
        container
        direction={"column"}
        alignItems="center"
        sx={styles.gridContainer}
      >
        <Grid item xs={4} sx={styles.logoContainer}>
          <img
            src={
              process.env.PUBLIC_URL +
              "/assets/images/highnotelogo-gradient.svg"
            }
            className="highnote-logo"
            alt="Highnote Logo"
          />
        </Grid>

        <Grid
          item
          xs={1}
          sx={{
            pb: 2,
          }}
        >
          <span className="title">
            <strong>{"Highnote"}</strong>
          </span>
        </Grid>

        <Grid item xs={1}>
          <span className="slogan">
            <strong>{"It pays to pay it forward."}</strong>
          </span>
        </Grid>
        <Grid item xs={6} sx={styles.buttonsContainer}>
          {buttonsContainer}
        </Grid>
      </Grid>
    </Container>
  );
};

export default HomePage;
